import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * HeadDedupe — synchronous SEO head safety net (Sep 2026, deterministic v2).
 *
 * Ownership model this enforces:
 *   - Prerender snapshots (journalSeoRenderer.js) inject route-specific head
 *     tags with data-rh="true" so react-helmet-async adopts/replaces them.
 *   - Page-level <SEO/> components are the Helmet source of truth.
 *   - The App-level <SeoFallback/> (App.js) does NOT render on routes whose
 *     page owns its SEO, so no competing fallback tags are ever emitted.
 *   - BlogPosting JSON-LD is owned by id (#article-blogposting-jsonld) via
 *     upsertArticleJsonLd — outside Helmet by design.
 *
 * This component is the residual safety net. It runs SYNCHRONOUSLY in a
 * useLayoutEffect keyed on the route pathname (before paint, at the exact
 * commit where navigation happens) and is additionally invoked directly by
 * SEO-owning pages at the moment their metadata-bearing data loads
 * (BlogDetail, SchoolGroupDetail). No timers. No MutationObserver. Every run
 * is a pure function of the current DOM: when a field is duplicated, the
 * React-owned (Helmet) tag wins and server-snapshot copies are removed;
 * React-owned nodes are never removed (see isReactOwned).
 */
const SINGLE_TAG_SELECTORS = [
  "title",
  'link[rel="canonical"]',
  'meta[name="description"]',
  // robots: collapse any conflicting duplicate directives (keep-last =
  // the route-level owner's directive wins, e.g. /sekolah/usulkan noindex).
  'meta[name="robots"]',
];

const OG_PROPERTIES = [
  "og:title", "og:description", "og:url", "og:image",
  "og:type", "og:site_name", "og:locale",
  "og:image:width", "og:image:height",
];

const TW_NAMES = [
  "twitter:card", "twitter:title", "twitter:description", "twitter:image",
];

const JSON_LD_SELECTOR = 'script[type="application/ld+json"]';

// React 19 + react-helmet-async v3: Helmet tags are React-owned hoisted
// elements. Removing one makes React crash on unmount ("removeChild of null"),
// so only NON-React nodes (server snapshot tags) are ever removed.
function isReactOwned(el) {
  return Object.keys(el).some((k) => k.startsWith("__react"));
}

function removeNode(el) {
  if (el.parentNode) el.parentNode.removeChild(el);
}

function keepOnlyLast(head, selector) {
  const els = Array.from(head.querySelectorAll(selector));
  if (els.length < 2) return;
  const owned = els.filter(isReactOwned);
  if (owned.length) els.filter((el) => !isReactOwned(el)).forEach(removeNode);
  else els.slice(0, -1).forEach(removeNode);
}

// Server snapshot head tags describe the initially-loaded URL only; once the
// SPA navigates away they are stale and must go (React-owned tags stay).
export function dropSnapshotHead() {
  const head = document.head;
  if (!head) return;
  head.querySelectorAll('title[data-rh], meta[data-rh], link[data-rh]').forEach((el) => {
    if (!isReactOwned(el)) removeNode(el);
  });
}

/** JSON-LD is additive per schema.org (BlogPosting + BreadcrumbList may
 *  legitimately coexist) — collapse only duplicates OF THE SAME @type,
 *  keeping the last occurrence. Elements with a stable id are additionally
 *  guaranteed unique by id. */
function dedupeJsonLd(head) {
  const nodes = Array.from(head.querySelectorAll(JSON_LD_SELECTOR));
  const byId = new Map();
  const byType = new Map();
  for (const n of nodes) {
    if (n.id) {
      if (!byId.has(n.id)) byId.set(n.id, []);
      byId.get(n.id).push(n);
    }
    let type = "__unknown__";
    try {
      const parsed = JSON.parse(n.textContent || "{}");
      type = String((parsed && parsed["@type"]) || "__unknown__");
    } catch { /* malformed JSON-LD — leave untouched */ }
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type).push(n);
  }
  const dropExtra = (arr) => {
    for (let i = 0; i < arr.length - 1; i++) {
      if (!isReactOwned(arr[i])) removeNode(arr[i]);
    }
  };
  byId.forEach(dropExtra);
  byType.forEach(dropExtra);
}

export function dedupeHeadNow() {
  const head = document.head;
  if (!head) return;
  SINGLE_TAG_SELECTORS.forEach((sel) => keepOnlyLast(head, sel));
  OG_PROPERTIES.forEach((p) => keepOnlyLast(head, `meta[property="${p}"]`));
  TW_NAMES.forEach((n) => keepOnlyLast(head, `meta[name="${n}"]`));
  dedupeJsonLd(head);
}

export default function HeadDedupe() {
  const { pathname } = useLocation();
  const initialPath = useRef(pathname);
  // useLayoutEffect → runs synchronously after DOM mutations, before paint,
  // at exactly the commit where the route changed.
  useLayoutEffect(() => {
    if (pathname !== initialPath.current) {
      initialPath.current = null;
      dropSnapshotHead();
    }
    dedupeHeadNow();
  }, [pathname]);
  return null;
}
